// =============================================================================
// HealthSherpa colours (5.18.0, 5.18.1) — EVERY colour of the plan list, plan view and Compare in one file so
// Henry can change any of them here. Each entry has a light and a dark value. Formal and calm: muted
// hues, soft tints, dark readable badge text (>= 4.5:1, checked by hsColors.test.mjs).
//
// Carriers: the 7 main carriers (the ones with logos) get clearly different MUTED hues — never red, rose
// or gold (those mean bronze / gold / HMO). Every other carrier is neutral gray. Matched like the logos:
// HIOS issuer id first (first 5 digits), then a normalized-name fallback. Ids are Solutions' TX carriers
// (from the first live quote + the uploaded logos, 2026-10-08) — a fork changes them here.
// =============================================================================

export const CARRIERS = [
  { key: 'bcbs_tx', short: 'Blue Cross TX', label: 'Blue Cross and Blue Shield of Texas', ids: ['33602'], nameTokens: ['blue', 'cross', 'shield'], light: '#4a6489', dark: '#859dbd' },
  { key: 'ambetter', short: 'Ambetter', label: 'Ambetter (Superior)', ids: ['29418', '87226', '43480'], nameTokens: ['ambetter'], light: '#479662', dark: '#7ec295' },
  { key: 'community_health_choice', short: 'Community Health Choice', label: 'Community Health Choice', ids: ['11718', '27248'], nameTokens: ['community', 'choice'], light: '#799357', dark: '#b7c8a2' },
  { key: 'imperial', short: 'Imperial', label: 'Imperial', ids: ['34826'], nameTokens: ['imperial'], light: '#6a425c', dark: '#cdafc3' },
  { key: 'oscar', short: 'Oscar', label: 'Oscar', ids: ['20069'], nameTokens: ['oscar'], light: '#5f3c87', dark: '#9f7ec6' },
  { key: 'unitedhealthcare', short: 'UnitedHealthcare', label: 'UnitedHealthcare', ids: ['40220', '70754', '98809'], nameTokens: ['unitedhealthcare'], light: '#4f8792', dark: '#abccd2' },
  { key: 'wellpoint', short: 'Wellpoint', label: 'Wellpoint', ids: ['47501'], nameTokens: ['wellpoint'], light: '#8a3a76', dark: '#c97eb7' },
];
export const CARRIER_OTHER = { key: 'other', label: 'Other carriers', light: '#8a8f98', dark: '#7d838c' };

// Same words the backend's logo matcher ignores (services/healthsherpa/logos.py GENERIC_WORDS).
const GENERIC = new Set(['of', 'and', 'the', 'inc', 'co', 'company', 'companies', 'insurance', 'health', 'healthcare',
  'plan', 'plans', 'healthplan', 'corp', 'corporation', 'llc', 'from', 'texas', 'tx', 'group']);
const tokens = (s) => String(s || '').toLowerCase().replace(/&/g, ' and ').split(/[^a-z0-9]+/).filter((t) => t && !GENERIC.has(t));
const issuerKey = (id) => { const m = /^\s*(\d{5})/.exec(String(id || '')); return m ? m[1] : null; };

export function carrierFor(plan) {
  const k = issuerKey(plan && plan.issuer_id);
  if (k) {
    const byId = CARRIERS.find((c) => c.ids.includes(k));
    if (byId) return byId;
  }
  const t = new Set(tokens(plan && plan.issuer_name));
  return CARRIERS.find((c) => c.nameTokens.every((x) => t.has(x))) || CARRIER_OTHER;
}

export const carrierColor = (plan, dark) => { const c = carrierFor(plan); return dark ? c.dark : c.light; };

// 5.20.1: the short carrier name for the one-line Clients list (the main carriers' `short`; any other carrier keeps
// its own name). Takes the same {issuer_id, issuer_name} as carrierFor.
export const carrierShortName = (plan) => {
  const c = carrierFor(plan);
  return c.short || String((plan && plan.issuer_name) || '').trim() || '—';
};

// Metal badges: filled, dark readable text, small rounded. Unknown codes -> plain text, no badge.
// 5.18.1: Gold / Silver / Platinum get a subtle two-tone SHEEN — a top-to-bottom gradient from `bg` to `bg2`
// with a thin `border` — and the text stays >= 4.5:1 against BOTH ends (hsColors.test.mjs). Entries without
// `bg2` are a flat fill. Catastrophic is a muted BLUE fill (filled = metal; EPO/POS are an OUTLINED blue).
// Platinum is a cool lavender-gray so it never reads as Silver (neutral gray) or Catastrophic (blue).
export const METALS = {
  gold: { light: { bg: '#f7e6a6', bg2: '#dcb54e', border: '#b8902c', text: '#3b2c00' },
    dark: { bg: '#f3dc8a', bg2: '#cfa63f', border: '#e9c968', text: '#2a1f00' } },
  silver: { light: { bg: '#f3f5f7', bg2: '#d3d8de', border: '#aab2bc', text: '#27313c' },
    dark: { bg: '#e2e6ea', bg2: '#b8c0c9', border: '#d0d6dd', text: '#1c242d' } },
  platinum: { light: { bg: '#f4f2f9', bg2: '#d6d0e6', border: '#aea5c6', text: '#2e2747' },
    dark: { bg: '#ebe8f3', bg2: '#c3bbd8', border: '#d9d3e8', text: '#221c38' } },
  bronze: { light: { bg: '#f3e1e5', text: '#6b3341' }, dark: { bg: '#ddbcc4', text: '#3e1a24' } },
  expanded_bronze: { light: { bg: '#e7c6ce', text: '#5a2533' }, dark: { bg: '#c99aa6', text: '#2c0f17' } },
  catastrophic: { light: { bg: '#c3d8f2', text: '#173a68' }, dark: { bg: '#8fb3e0', text: '#0c2242' } },
};
// CSS background of a metal badge tone (gradient when it has a sheen).
export const metalFill = (t) => (t.bg2 ? `linear-gradient(180deg, ${t.bg} 0%, ${t.bg2} 100%)` : t.bg);

// Plan-type badges: OUTLINED (transparent fill), capitals — never mistaken for a filled metal badge.
// HMO is a brick red (hue ~10°) — Bronze is a pale ROSE fill (hue ~345°): outline vs fill AND a different hue.
export const PLAN_TYPES = {
  hmo: { light: '#a1452f', dark: '#e59a86' },
  ppo: { light: '#336a41', dark: '#8fcb9f' },          // darkened from #3a7449 (4.46:1 on ticked+hover)
  other: { light: '#3b6595', dark: '#93b6e0' },          // EPO, POS and anything else
};
export const planTypeColors = (code) => PLAN_TYPES[String(code || '').toLowerCase()] || PLAN_TYPES.other;

// Rows: page/panel background, hover, ticked, ticked+hover, divider, plan-name cell border, header rule.
// 5.18.1: the row divider is a clearly visible 1px line (>= 2.5:1 against every row background, so it stays
// visible on hovered and ticked rows; the 5.18.0 #e2e5e9 was ~1.3:1). `headRule` is the 2px line under
// the column headers. text / subtext are >= 4.5:1 on every row background (tested).
export const ROW = {
  panel: { light: '#ffffff', dark: '#242424' },
  hover: { light: '#f3f5f8', dark: '#2c3036' },
  tick: { light: '#e9eff6', dark: '#1f2836' },
  tickHover: { light: '#dfe7f1', dark: '#27334a' },
  divider: { light: '#8a929d', dark: '#737a84' },
  headRule: { light: '#4a525c', dark: '#a3aab4' },
  nameBorder: { light: '#c9ced6', dark: '#4f545b' },
  text: { light: '#1a1a1a', dark: '#f0f0f0' },
  subtext: { light: '#4f5761', dark: '#b4bac2' },
};
export const rowTone = (name, dark) => ROW[name][dark ? 'dark' : 'light'];

// Calm chips for the results summary (cached / quote type) — outlined, muted.
export const CHIP = { light: '#5d6570', dark: '#aab0b8' };

// --- WCAG contrast (used by the test, and handy when Henry changes a colour) ---
const lin = (c) => { const v = c / 255; return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4; };
export function luminance(hex) {
  const h = hex.replace('#', '');
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}
export function contrast(a, b) {
  const [x, y] = [luminance(a), luminance(b)].sort((p, q) => q - p);
  return (x + 0.05) / (y + 0.05);
}

// --- 5.19.0 phase 2: policy status badges + the alerts box (formal, muted; text >= 4.5:1, tested) ---
// active = effectuated / paid; attention = unpaid first payment, past due, sources disagree;
// ended = cancelled / terminated; neutral = anything else (pending, unknown). `danger` only for a failed or
// missing morning refresh.
export const POLICY_TONES = {
  active: { light: { bg: '#e3efe6', text: '#234d31', border: '#a9c9b2' }, dark: { bg: '#263a2c', text: '#bfe0c8', border: '#3f6249' } },
  attention: { light: { bg: '#f8eed8', text: '#5e4510', border: '#d9b45c' }, dark: { bg: '#3a3019', text: '#ecd49c', border: '#6e5a2a' } },
  ended: { light: { bg: '#f1e4e4', text: '#5e2b2b', border: '#cfa5a5' }, dark: { bg: '#3a2626', text: '#e8c0c0', border: '#6a4040' } },
  neutral: { light: { bg: '#e8eaee', text: '#333c47', border: '#c3c9d1' }, dark: { bg: '#30343a', text: '#d4d8de', border: '#4c525a' } },
  danger: { light: { bg: '#f7e1e1', text: '#7a1f1f', border: '#d99a9a' }, dark: { bg: '#422222', text: '#f2c2c2', border: '#7a3b3b' } },
};
const ACTIVE_WORDS = ['active', 'effectuated', 'paid'];
const ENDED_WORDS = ['cancelled', 'canceled', 'terminated'];
const ATTENTION_WORDS = ['unpaid_binder', 'past_due', 'grace', 'delinquent'];
// The tone for an API status / payment-status value (or an export policy_status).
export function policyTone(value) {
  const v = String(value || '').toLowerCase();
  if (ATTENTION_WORDS.some((w) => v.includes(w))) return 'attention';
  if (ENDED_WORDS.includes(v)) return 'ended';
  if (ACTIVE_WORDS.includes(v)) return 'active';
  return 'neutral';
}
export const policyColors = (tone, dark) => (POLICY_TONES[tone] || POLICY_TONES.neutral)[dark ? 'dark' : 'light'];

// --- 5.20.4: the Clients list's ZEBRA rows (Henry 2026-10-11) ------------------------------------------------
// Rows alternate base / alt in the order they are shown (the row's index on the page — so sorting, filtering and
// paging never leave a gap or a double, and an open drop-down doesn't shift the rows below it). `hover` is the
// hovered, keyboard-focused and OPEN row (and the drop-down under it): a light blue-gray (dark: a slate) that
// stands apart from BOTH stripes. `divider` is the 2px line between rows (unchanged from 5.20.1, now named here).
// Tested in hsColors.test.mjs: text / subtext >= 4.5:1 and the divider >= 2.5:1 on every one of them, hover vs
// each stripe, and the search highlights >= 2:1 on the background they sit on.
export const CLIENT_ROW = {
  base: { light: '#ffffff', dark: '#242424' },          // = ROW.panel
  alt: { light: '#f3f5f8', dark: '#2c2c2c' },
  hover: { light: '#dbe4f0', dark: '#353e4b' },
  divider: { light: '#4a525c', dark: '#8a919b' },
};
export const clientRowTone = (name, dark) => CLIENT_ROW[name][dark ? 'dark' : 'light'];

// --- 5.20.1: search highlight (<mark>) in the Clients list and its drop-down — subtle, text >= 4.5:1 (tested) ---
// 5.20.4: the light resting mark is a deeper gold (#f5d76e was 1.42:1 on white and 1.30:1 on the alt stripe; now
// >= 2:1 on both stripes, tested). Dark unchanged.
export const HIGHLIGHT = {
  light: { bg: '#d4a726', text: '#1a1a1a' },
  dark: { bg: '#6e5b1c', text: '#fff7dc' },
};
export const highlightColors = (dark) => HIGHLIGHT[dark ? 'dark' : 'light'];
// 5.20.3: on a HOVERED / keyboard-focused / open row (and in its drop-down) the mark is a stronger gold with a
// 1px underline: >= 2:1 against the hovered row (5.20.4: CLIENT_ROW.hover) and text >= 4.5:1 (tested). In dark mode it is a BRIGHTER gold — a
// darker one can't stand out from the dark hovered row.
export const HIGHLIGHT_HOVER = {
  light: { bg: '#c79a1e', text: '#1a1a1a' },
  dark: { bg: '#a8892c', text: '#1a1a1a' },
};
export const highlightHoverColors = (dark) => HIGHLIGHT_HOVER[dark ? 'dark' : 'light'];
